import type { InvalidateQueryFilters, QueryClient } from '@tanstack/react-query';
import { tableEditingStore } from '@/store/table-editing.store';
import type { DeferredEntityType } from '@/store/table-editing.store';

/**
 * Guards that keep remote (realtime) cache updates from disrupting active user
 * interactions, while letting everything else stay live.
 *
 * Principles:
 * - A remote row patch is only deferred when THAT row has an open editor.
 *   Patches to other rows preserve row identity, so memoized rows don't
 *   re-render and open popovers elsewhere are unaffected.
 * - A list refetch (invalidate) replaces the whole array and can move or
 *   remove the row under an open editor, so refetches are deferred while ANY
 *   editor is open and flushed as soon as the last one closes. The query is
 *   still marked stale immediately so no staleness is ever lost.
 * - The user's own mutations must never be deferred — these guards are for
 *   remote updates only.
 */

/**
 * Defer a remote row update if that specific row currently has an open editor.
 * Returns true when the update was queued (caller should skip applying it).
 */
export function deferRowUpdateIfEditing(
  entityType: DeferredEntityType,
  partial: Record<string, unknown> & { id: string },
): boolean {
  const state = tableEditingStore.getState();
  if (!state.hasOpenPopoverForRow(partial.id)) return false;
  state.queueDeferredUpdate(entityType, partial.id, partial);
  return true;
}

/**
 * Subscribe to the editing store and replay deferred updates for an entity
 * type as soon as the affected row's editors close (per-row, not global).
 * Returns an unsubscribe function.
 */
export function flushDeferredRowUpdates(
  entityType: DeferredEntityType,
  apply: (update: Record<string, unknown> & { id: string }) => void,
): () => void {
  let scheduled = false;
  let unsubscribed = false;

  const tryFlush = () => {
    scheduled = false;
    if (unsubscribed) return;
    const state = tableEditingStore.getState();
    const deferred = state.getDeferredUpdatesByType(entityType);
    for (const [rowId, update] of deferred) {
      if (state.hasOpenPopoverForRow(rowId)) continue;
      state.clearDeferredUpdate(rowId);
      apply({ id: rowId, ...update.updates });
    }
  };

  const unsubscribe = tableEditingStore.subscribe((state) => {
    if (scheduled || state.deferredUpdates.size === 0) return;
    scheduled = true;
    // Microtask avoids mutating the store synchronously inside its own
    // subscriber notification.
    queueMicrotask(tryFlush);
  });

  // Initial flush: recover updates queued while no subscriber was mounted —
  // e.g. the user navigated away with an editor open, the close notification
  // scheduled a flush, but the unmounting subscriber's microtask bailed on
  // `unsubscribed`. Without this, those updates linger until purged.
  if (tableEditingStore.getState().deferredUpdates.size > 0) {
    scheduled = true;
    queueMicrotask(tryFlush);
  }

  return () => {
    unsubscribed = true;
    unsubscribe();
  };
}

interface PendingRefetch {
  queryClient: QueryClient;
  filters: InvalidateQueryFilters;
}

const pendingRefetches = new Map<string, PendingRefetch>();
let refetchFlushSubscribed = false;

function ensureRefetchFlushSubscription(): void {
  if (refetchFlushSubscribed) return;
  refetchFlushSubscribed = true;

  let scheduled = false;
  const tryFlush = () => {
    scheduled = false;
    const state = tableEditingStore.getState();
    if (state.hasAnyOpenPopover() || pendingRefetches.size === 0) return;
    const entries = [...pendingRefetches.values()];
    pendingRefetches.clear();
    for (const { queryClient, filters } of entries) {
      void queryClient.invalidateQueries(filters);
    }
  };

  tableEditingStore.subscribe(() => {
    if (scheduled || pendingRefetches.size === 0) return;
    scheduled = true;
    queueMicrotask(tryFlush);
  });
}

/**
 * Invalidate queries from a realtime event without disrupting active editing.
 * While any editor is open the queries are marked stale but not refetched;
 * the refetch fires as soon as the last editor closes.
 */
export function safeInvalidateQueries(
  queryClient: QueryClient,
  filters: InvalidateQueryFilters,
): void {
  if (tableEditingStore.getState().hasAnyOpenPopover()) {
    void queryClient.invalidateQueries({ ...filters, refetchType: 'none' });
    pendingRefetches.set(JSON.stringify(filters.queryKey ?? filters), {
      queryClient,
      filters,
    });
    ensureRefetchFlushSubscription();
    return;
  }
  void queryClient.invalidateQueries(filters);
}
